<script setup>
import { ref, computed, onMounted } from 'vue';
import instagramLeadScoreApi from 'dashboard/api/instagramLeadScore';

const props = defineProps({
  contact: {
    type: Object,
    required: true,
  },
});

const leadScore = ref(null);
const isLoading = ref(false);
const isAnalyzing = ref(false);
const copied = ref(false);

const isInstagramFollower = computed(
  () => props.contact?.additional_attributes?.instagram_follower
);

const username = computed(
  () => props.contact?.additional_attributes?.social_instagram_user_name
);

const isInstagramContact = computed(
  () => isInstagramFollower.value || !!username.value
);

const scoreColor = computed(() => {
  if (!leadScore.value) return 'text-n-slate-10';
  const score = leadScore.value.score;
  if (score >= 8) return 'text-emerald-600';
  if (score >= 6) return 'text-amber-500';
  return 'text-red-500';
});

const scoreBg = computed(() => {
  if (!leadScore.value) return 'bg-n-alpha-2';
  const score = leadScore.value.score;
  if (score >= 8) return 'bg-emerald-50 border-emerald-200';
  if (score >= 6) return 'bg-amber-50 border-amber-200';
  return 'bg-red-50 border-red-200';
});

const isQualified = computed(
  () => leadScore.value?.qualification_label === 'QUALIFICADO'
);

const scoreBarWidth = computed(() => {
  if (!leadScore.value?.score) return '0%';
  return `${leadScore.value.score * 10}%`;
});

const instagramProfileUrl = computed(() => {
  if (!username.value) return null;
  return `https://www.instagram.com/${username.value}/`;
});

async function loadLeadScore() {
  if (!props.contact?.id) return;
  isLoading.value = true;
  try {
    const response = await instagramLeadScoreApi.get(props.contact.id);
    leadScore.value = response.data;
  } catch {
    leadScore.value = null;
  } finally {
    isLoading.value = false;
  }
}

async function analyze() {
  isAnalyzing.value = true;
  try {
    await instagramLeadScoreApi.analyze(props.contact.id);
    // Poll after a few seconds for result
    setTimeout(() => {
      loadLeadScore();
      isAnalyzing.value = false;
    }, 8000);
  } catch {
    isAnalyzing.value = false;
  }
}

function copyMessage() {
  if (!leadScore.value?.suggested_message) return;
  navigator.clipboard.writeText(leadScore.value.suggested_message);
  copied.value = true;
  setTimeout(() => {
    copied.value = false;
  }, 2000);
}

onMounted(() => {
  if (isInstagramContact.value) {
    loadLeadScore();
  }
});
</script>

<template>
  <div v-if="isInstagramContact" class="mt-3">
    <div class="rounded-lg border border-n-alpha-3 bg-n-solid-1 p-3">
      <div class="mb-2 flex items-center justify-between">
        <div class="flex items-center gap-1.5">
          <span class="i-lucide-instagram text-sm text-pink-500" />
          <p class="text-xs font-semibold text-n-slate-12">
            Instagram Lead Score
          </p>
        </div>
        <a
          v-if="instagramProfileUrl"
          :href="instagramProfileUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="text-xs text-n-blue-9 hover:text-n-blue-11"
          title="Ver perfil"
        >
          @{{ username }}
          <span class="i-lucide-external-link ml-0.5 text-[10px]" />
        </a>
      </div>

      <!-- Loading -->
      <div v-if="isLoading" class="py-4 text-center">
        <span class="i-lucide-loader-2 animate-spin text-lg text-n-slate-10" />
      </div>

      <!-- No score yet -->
      <div v-else-if="!leadScore" class="text-center">
        <p class="mb-2 text-xs text-n-slate-10">Perfil ainda não analisado</p>
        <button
          type="button"
          class="inline-flex items-center gap-1 rounded-md bg-n-blue-9 px-3 py-1.5 text-xs font-medium text-white hover:bg-n-blue-10 disabled:opacity-50"
          :disabled="isAnalyzing"
          @click="analyze"
        >
          <span
            :class="
              isAnalyzing
                ? 'i-lucide-loader-2 animate-spin'
                : 'i-lucide-sparkles'
            "
            class="text-sm"
          />
          {{ isAnalyzing ? 'Analisando...' : 'Analisar perfil' }}
        </button>
      </div>

      <!-- Score result -->
      <div v-else>
        <div class="rounded-lg border p-2.5" :class="[scoreBg]">
          <!-- Score bar -->
          <div class="mb-2 flex items-center justify-between">
            <span class="text-lg font-bold" :class="[scoreColor]">
              {{ leadScore.score }}/10
            </span>
            <span
              class="rounded-full px-2 py-0.5 text-xs font-semibold"
              :class="[
                isQualified
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-red-100 text-red-700',
              ]"
            >
              {{ isQualified ? '✓ QUALIFICADO' : '✗ NÃO QUALIFICADO' }}
            </span>
          </div>
          <div
            class="mb-2 h-1.5 w-full overflow-hidden rounded-full bg-white/60"
          >
            <div
              class="h-full rounded-full transition-all duration-500"
              :class="[
                leadScore.score >= 8
                  ? 'bg-emerald-500'
                  : leadScore.score >= 6
                    ? 'bg-amber-400'
                    : 'bg-red-400',
              ]"
              :style="{ width: scoreBarWidth }"
            />
          </div>
          <p class="text-xs text-n-slate-11">
            {{ leadScore.qualification_reason }}
          </p>
        </div>

        <!-- Suggested message -->
        <div v-if="leadScore.suggested_message" class="mt-2">
          <p class="mb-1 text-xs font-medium text-n-slate-10">
            Mensagem sugerida:
          </p>
          <div
            class="rounded-md border border-n-alpha-3 bg-n-alpha-1 p-2 text-xs text-n-slate-11"
          >
            {{ leadScore.suggested_message }}
          </div>
          <div class="mt-1.5 flex gap-1.5">
            <button
              type="button"
              class="inline-flex items-center gap-1 rounded-md border border-n-alpha-3 px-2 py-1 text-xs text-n-slate-11 hover:bg-n-alpha-2"
              @click="copyMessage"
            >
              <span
                :class="
                  copied ? 'i-lucide-check text-emerald-500' : 'i-lucide-copy'
                "
                class="text-sm"
              />
              {{ copied ? 'Copiado!' : 'Copiar' }}
            </button>
            <button
              type="button"
              class="inline-flex items-center gap-1 rounded-md border border-n-alpha-3 px-2 py-1 text-xs text-n-slate-11 hover:bg-n-alpha-2 disabled:opacity-50"
              :disabled="isAnalyzing"
              @click="analyze"
            >
              <span
                :class="
                  isAnalyzing
                    ? 'i-lucide-loader-2 animate-spin'
                    : 'i-lucide-refresh-cw'
                "
                class="text-sm"
              />
              {{ isAnalyzing ? 'Analisando...' : 'Reanalisar' }}
            </button>
          </div>
        </div>

        <p class="mt-1.5 text-right text-[10px] text-n-slate-9">
          Analisado
          {{
            leadScore.analyzed_at
              ? new Date(leadScore.analyzed_at).toLocaleDateString('pt-BR')
              : ''
          }}
        </p>
      </div>
    </div>
  </div>
</template>
